// features/ApprovalRequest/ApprovalRequestListView/index.tsx
import { FC, useEffect } from 'react';
import {
  Box,
  Container,
  IconButton,
  InputAdornment,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Autocomplete,
  TextField,
  Tooltip,
} from '@mui/material';
import PageGrid from 'components/PageGrid';
import { observer } from "mobx-react";
import store from "./store";
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import PopupGrid from 'components/PopupGrid';
import ApprovalRequestPopupForm from './../ApprovalRequestAddEditView/popupForm';
import CustomTextField from 'components/TextField';
import CustomButton from 'components/Button';
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import DateField from 'components/DateField';
import LookUp from 'components/LookUp';
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";

type ApprovalRequestListViewProps = {};

const ApprovalRequestListView: FC<ApprovalRequestListViewProps> = observer((props) => {
  const { t } = useTranslation();
  const translate = t;

  useEffect(() => {
    store.loadApprovalRequests();
    store.loadOperators();
    store.loadPriorities();
    store.loadStatuses();
    return () => {
      store.clearStore();
    }
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'executor_names',
      headerName: 'Исполнители',
      flex: 1.5,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="flex-start" gap={0.5} width="100%" py={0.5}>
          <Box flex={1}>
            {params.row.executor_names ? (
              (params.row.executor_names as string).split(', ').map((name: string, i: number) => (
                <Typography key={i} variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
                  {name}
                </Typography>
              ))
            ) : (
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.disabled', fontStyle: 'italic', lineHeight: 1.6 }}>
                Не назначено
              </Typography>
            )}
          </Box>
          <Tooltip title="Назначить исполнителей">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); store.openExecutorDialog(params.row.id); }}>
              <GroupAddIcon fontSize="small" color={params.row.executor_names ? 'primary' : 'action'} />
            </IconButton>
          </Tooltip>
        </Box>
      )
    },
    {
      field: 'bga_application_number',
      headerName: translate("label:ApprovalRequestListView.bga_application_number"),
      flex: 1.5
    },
    {
      field: 'applicant_name',
      headerName: translate("label:ApprovalRequestListView.applicant_name"),
      flex: 1.5
    },
    {
      field: 'approval_type',
      headerName: translate("label:ApprovalRequestListView.approval_type"),
      flex: 1
    },
    {
      field: 'status_name',
      headerName: translate("label:ApprovalRequestListView.current_status"),
      flex: 1,
      renderCell: (params) => {
        return (
          <span style={{
            color: '#'+ params.row.status_color || '#000000',
            fontWeight: 'bold'
          }}>
            {params.value}
          </span>
        );
      }
    },
    {
      field: 'priority',
      headerName: translate("label:ApprovalRequestListView.priority"),
      flex: 0.8
    },
    {
      field: 'received_date',
      headerName: translate("label:ApprovalRequestListView.received_date"),
      flex: 1,
      renderCell: (params) => {
        return params.value ? new Date(params.value).toLocaleDateString() : '';
      }
    },
    {
      field: 'deadline_date',
      headerName: translate("label:ApprovalRequestListView.deadline_date"),
      flex: 1,
      renderCell: (params) => {
        return params.value ? new Date(params.value).toLocaleDateString() : '';
      }
    },
  ];

  let type1: string = 'form';
  let component = null;
  switch (type1) {
    case 'form':
      component = <PageGrid
        title={translate("label:ApprovalRequestListView.entityTitle")}
        onDeleteClicked={(id: number) => store.deleteApprovalRequest(id)}
        columns={columns}
        data={store.data}
        hideDeleteButton={true}
        hideAddButton={true}
        tableName="ApprovalRequest"
        getRowHeight={() => 'auto'} />
      break;
    case 'popup':
      component = <PopupGrid
        title={translate("label:ApprovalRequestListView.entityTitle")}
        onDeleteClicked={(id: number) => store.deleteApprovalRequest(id)}
        onEditClicked={(id: number) => store.onEditClicked(id)}
        columns={columns}
        data={store.data}
        tableName="ApprovalRequest" />
      break;
  }

  return (
    <Container maxWidth='xl' >

      {/* Форма фильтра */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            {translate("label:ApprovalRequestListView.filterTitle")}
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}  >
            <CustomTextField
              value={store.filterParams.bga_application_number}
              onChange={(e) => store.updateFilterParam('bga_application_number', e.target.value)}
              label={translate("label:ApprovalRequestListView.bga_application_number")}
              name="filter_bga_application_number"
              id="filter_bga_application_number"
            />
          </Grid>

          <Grid item xs={12} sm={12}  >
            <CustomTextField
              value={store.filterParams.applicant_name}
              onChange={(e) => store.updateFilterParam('applicant_name', e.target.value)}
              label={translate("label:ApprovalRequestListView.applicant_name")}
              name="filter_applicant_name"
              id="filter_applicant_name"
            />
          </Grid>

          <Grid item xs={12} sm={12}  >
            <LookUp
              data={store.priorities}
              value={store.filterParams.priority}
              onChange={(e) => store.updateFilterParam('priority', e.target.value)}
              label={translate("label:ApprovalRequestListView.priority")}
              name="filter_priority"
              id="filter_priority"
            />
          </Grid>
          <Grid item xs={12} sm={12}  >
            <LookUp
              data={store.statuses}
              value={store.filterParams.status_id}
              onChange={(e) => store.updateFilterParam('status_id', e.target.value)}
              label={translate("label:ApprovalRequestListView.status_id")}
              name="filter_status"
              id="filter_status"
            />
          </Grid>

          {/* <Grid item xs={12} sm ={12}  >
            <LookUp
              data={store.operators}
              value={store.filterParams.operator_id}
              onChange={(e) => store.updateFilterParam('operator_id', e.target.value)}
              label={translate("label:ApprovalRequestListView.operator")}
              name="filter_operator_id"
              id="filter_operator_id"
            />
          </Grid> */}

          <Grid item xs={12} sm={12}  >
            <DateField
              value={store.filterParams.received_date_from}
              onChange={(e) => store.updateFilterParam('received_date_from', e.target.value)}
              label={translate("label:ApprovalRequestListView.received_date_from")}
              name="filter_received_date_from"
              id="filter_received_date_from"
              helperText=""
            />
          </Grid>

          <Grid item xs={12} sm={12}  >
            <DateField
              value={store.filterParams.received_date_to}
              onChange={(e) => store.updateFilterParam('received_date_to', e.target.value)}
              label={translate("label:ApprovalRequestListView.received_date_to")}
              name="filter_received_date_to"
              id="filter_received_date_to"
              helperText=""
            />
          </Grid>
        </Grid>

        <Box mt={2} display="flex" gap={1}>
          <CustomButton
            variant='contained'
            startIcon={<SearchIcon />}
            onClick={() => store.applyFilter()}
          >
            {translate("common:search")}
          </CustomButton>
          <CustomButton
            variant='outlined'
            startIcon={<ClearIcon />}
            onClick={() => store.clearFilter()}
          >
            {translate("common:clear")}
          </CustomButton>
        </Box>
      </Paper>

      {component}

      <ApprovalRequestPopupForm
        openPanel={store.openPanel}
        id={store.currentId}
        onBtnCancelClick={() => store.closePanel()}
        onSaveClick={() => {
          store.closePanel();
          store.loadApprovalRequests();
        }}
      />

      {/* Диалог назначения исполнителей */}
      <Dialog
        open={store.executorDialogOpen}
        onClose={() => store.closeExecutorDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Исполнители запроса</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={store.executorAllUsers}
            getOptionLabel={(option: any) => option.full_name || ''}
            getOptionKey={(option: any) => option.id}
            renderOption={(props, option: any) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body2">{option.full_name}</Typography>
                  {(option.department_name || option.position) && (
                    <Typography variant="caption" color="text.secondary">
                      {[option.department_name, option.position].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            onChange={(_e, value) => {
              if (value) store.addExecutor((value as any).id);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Выбрать исполнителя"
                size="small"
                fullWidth
              />
            )}
            sx={{ mt: 1, mb: 2 }}
          />

          {store.executorDialogExecutors.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Исполнители не назначены
            </Typography>
          ) : (
            <List dense>
              {store.executorDialogExecutors.map((ex: any) => (
                <ListItem key={ex.id} divider>
                  <ListItemText
                    primary={ex.user_full_name}
                    secondary={[ex.user_position, ex.department_name].filter(Boolean).join(' · ')}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Удалить исполнителя">
                      <IconButton edge="end" size="small" onClick={() => store.removeExecutor(ex.id)}>
                        <PersonRemoveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton variant="outlined" onClick={() => { store.closeExecutorDialog(); store.loadApprovalRequests(); }}>
            Закрыть
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
});

export default ApprovalRequestListView;